import { computed, readonly, ref } from "vue";
import { useApi } from "~/composables/useApi";
import { ACCESS_TOKEN } from "~/enum/cache/cookie";
import { useMainStore } from "~/stores";
import Cookie from "~/utils/cookie";

/**
 * 用户信息管理 Composable
 */
export function useUserInfo() {
  const store = useMainStore();
  const api = useApi();
  const loading = ref(false);
  const error = ref<string | null>(null);

  /**
   * 获取用户信息
   * @param params 参数对象
   * @param params.refresh 是否强制刷新
   * @param params.type 请求类型，"ai" 表示AI接口
   * @returns Promise<any>
   */
  const getUserInfo = async (params: { refresh?: boolean; type?: string } = {}) => {
    const { refresh = false, type = "" } = params;

    // 检查 token
    if (!Cookie.get(ACCESS_TOKEN)) {
      await store.logout();
    }

    // 如果已登录且不需要刷新，直接返回缓存的信息
    if (store.login && !refresh) {
      return store.info;
    }

    loading.value = true;
    error.value = null;

    try {
      // 调用 API 获取用户信息
      const response = await api.getUserInfo(type);

      if (response.success) {
        const body = response.body;

        // 更新 store 中的用户信息
        store.updateUserInfo(body);

        return body;
      } else {
        console.error("获取用户信息失败:", response.msg);
        throw new Error("获取用户信息失败");
      }
    } catch (err: any) {
      error.value = err.message || "获取用户信息失败";
      console.error("获取用户信息失败:", err);
      throw err;
    } finally {
      loading.value = false;
    }
  };

  /**
   * 刷新用户信息
   * @param type 请求类型
   */
  const refreshUserInfo = async (type?: string) => {
    return getUserInfo({ refresh: true, type });
  };

  /**
   * 检查用户是否已登录
   */
  const checkAuth = async () => {
    try {
      await api.auth();
      return true;
    } catch {
      return false;
    }
  };

  return {
    // 状态
    loading: readonly(loading),
    error: readonly(error),

    // 方法
    getUserInfo,
    refreshUserInfo,
    checkAuth,

    // Store 状态的响应式引用
    userInfo: computed(() => store.info),
    isLoggedIn: computed(() => store.login),
    userName: computed(() => store.name),
    userAvatar: computed(() => store.avatar),
    userRole: computed(() => store.role),
    userType: computed(() => store.userType),
    corpList: computed(() => store.corpList),
  };
}
